import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Save, ArrowLeft, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Field, Input } from '../components/FormField';

export default function AdminProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '', middleName: '', lastName: '',
    email: '', phone: '',
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        firstName:  user.firstName  || '',
        middleName: user.middleName || '',
        lastName:   user.lastName   || '',
        email:      user.email      || '',
        phone:      user.phone      || '',
      });
    }
  }, [user]);

  const handleDetails = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) return toast.error('First name is required');
    if (!form.lastName.trim())  return toast.error('Last name is required');
    if (!form.email.trim())     return toast.error('Email is required');
    setLoading(true);
    try {
      const res = await api.put('/auth/update-profile', {
        firstName:  form.firstName,
        middleName: form.middleName,
        lastName:   form.lastName,
        email:      form.email,
        phone:      form.phone,
      });
      if (res.data.token) localStorage.setItem('token', res.data.token);
      await refreshUser();
      toast.success('Personal details updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating profile');
    } finally { setLoading(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.currentPassword) return toast.error('Enter your current password');
    if (!pwForm.newPassword)     return toast.error('Enter a new password');
    if (pwForm.newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    if (pwForm.newPassword !== pwForm.confirmPassword) return toast.error('Passwords do not match');
    setPwLoading(true);
    try {
      const res = await api.put('/auth/update-profile', {
        currentPassword: pwForm.currentPassword,
        newPassword:     pwForm.newPassword,
      });
      if (res.data.token) localStorage.setItem('token', res.data.token);
      await refreshUser();
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error changing password');
    } finally { setPwLoading(false); }
  };

  return (
    <div className="max-w-2xl space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors px-3 py-2 rounded-xl border border-gray-200 hover:border-gray-300 bg-white">
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Personal Details</h1>
          <p className="text-sm text-gray-400 mt-0.5">Update your account information</p>
        </div>
      </div>

      {/* Avatar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-md"
          style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
          {(form.firstName?.[0] || user?.name?.[0] || '?').toUpperCase()}
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-800">
            {[form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ') || user?.name}
          </p>
          <p className="text-sm text-gray-400">{form.email}</p>
          <span className="inline-block mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
            Master Admin
          </span>
        </div>
      </div>

      {/* Personal Details Form */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
          <User size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-700">Personal Information</h2>
        </div>
        <form onSubmit={handleDetails} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="First Name" required>
              <Input value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                placeholder="First name" />
            </Field>
            <Field label="Middle Name">
              <Input value={form.middleName} onChange={e => setForm(f => ({ ...f, middleName: e.target.value }))}
                placeholder="Middle name" />
            </Field>
            <Field label="Last Name" required>
              <Input value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name" />
            </Field>
          </div>
          <Field label="Email Address" required>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                type="email" placeholder="your@email.com"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
            </div>
          </Field>
          <Field label="Phone Number">
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                type="tel" placeholder="+91 00000 00000"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
            </div>
          </Field>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition-all"
              style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
              {loading ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
              {loading ? 'Saving…' : 'Update Details'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Form */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
          <Lock size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-700">Change Password</h2>
        </div>
        <form onSubmit={handlePassword} className="space-y-4">
          {[
            { label: 'Current Password', key: 'currentPassword', show: showCurrent, toggle: () => setShowCurrent(v => !v) },
            { label: 'New Password',     key: 'newPassword',     show: showNew,     toggle: () => setShowNew(v => !v) },
            { label: 'Re-enter New Password', key: 'confirmPassword', show: showConfirm, toggle: () => setShowConfirm(v => !v) },
          ].map(({ label, key, show, toggle }) => (
            <Field key={key} label={label} required>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={show ? 'text' : 'password'}
                  value={pwForm[key]}
                  onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors"
                />
                <button type="button" onClick={toggle}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
          ))}
          {pwForm.newPassword && pwForm.confirmPassword && (
            <div className={`flex items-center gap-2 text-xs ${pwForm.newPassword === pwForm.confirmPassword ? 'text-emerald-600' : 'text-rose-500'}`}>
              <CheckCircle size={12} />
              {pwForm.newPassword === pwForm.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={pwLoading}
              className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition-all"
              style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
              {pwLoading ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Lock size={14} />}
              {pwLoading ? 'Changing…' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
